import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { StarRating } from "@/components/StarRating";
import { Plus, Star, MapPin, BadgeCheck, Sparkles } from "lucide-react";

export default function DashboardPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", category: "", description: "", city: "", state: "",
    address: "", phone: "", website: "", image_url: "",
    is_professional: false, professional_title: "",
  });

  useEffect(() => {
    if (loading) return;
    if (!user || typeof user !== "object") { navigate("/login"); return; }
    if (user.role !== "business_owner" && user.role !== "admin") { navigate("/"); return; }
    load();
    api.get("/categories").then((r) => setCategories(r.data));
  }, [loading, user, navigate]);

  const load = async () => {
    const [b, r] = await Promise.all([
      api.get("/my/businesses"),
      api.get("/my/reviews"),
    ]);
    setBusinesses(b.data);
    setReviews(r.data);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/businesses", form);
      toast.success("Listing created");
      setOpen(false);
      setForm({ name: "", category: "", description: "", city: "", state: "", address: "", phone: "", website: "", image_url: "", is_professional: false, professional_title: "" });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create");
    } finally { setSubmitting(false); }
  };

  const upgradeListing = async (bizId) => {
    try {
      const { data } = await api.post("/payments/checkout", {
        business_id: bizId,
        lookup_key: "premium_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
    }
  };

  const openPortal = async (bizId) => {
    try {
      const { data } = await api.post("/payments/portal", {
        business_id: bizId,
        origin_url: window.location.origin,
      });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No active subscription");
    }
  };

  if (loading || !user) return <div className="p-16 text-slate-500">Loading…</div>;

  const totalReviews = businesses.reduce((s, b) => s + b.rating_count, 0);
  const avg = businesses.length
    ? (businesses.reduce((s, b) => s + b.rating_avg * b.rating_count, 0) / (totalReviews || 1)).toFixed(1)
    : "—";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-medium mb-2">Owner dashboard</div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Welcome, {user.name}</h1>
        </div>
        <Button className="bg-blue-700 hover:bg-blue-800" onClick={() => setOpen(true)} data-testid="dashboard-new-listing">
          <Plus className="h-4 w-4 mr-2" /> New listing
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard label="Listings" value={businesses.length} testid="stat-listings" />
        <StatCard label="Total reviews" value={totalReviews} testid="stat-reviews" />
        <StatCard label="Average rating" value={avg} testid="stat-avg" />
        <StatCard label="Verified" value={businesses.filter(b => b.verified).length} testid="stat-verified" />
      </div>

      {/* Listings */}
      <h2 className="text-2xl font-semibold text-slate-900 mb-4">Your listings</h2>
      {businesses.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center" data-testid="dashboard-empty">
          <p className="text-slate-500 mb-4">You don't own any listings yet. Create one or claim an existing business.</p>
          <div className="flex justify-center gap-3">
            <Button onClick={() => setOpen(true)} className="bg-blue-700 hover:bg-blue-800">Create listing</Button>
            <Button asChild variant="outline"><Link to="/directory">Find a business to claim</Link></Button>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4 mb-12" data-testid="dashboard-listings">
          {businesses.map((b) => (
            <div key={b.id} className="bg-white rounded-xl border border-slate-200 p-5 hover:border-blue-300 hover:shadow-md transition-all">
              <Link to={`/business/${b.id}`} className="flex gap-4">
                <div className="h-20 w-20 rounded-lg bg-slate-100 overflow-hidden shrink-0">
                  {b.image_url && <img src={b.image_url} className="w-full h-full object-cover" alt="" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-semibold text-slate-900 truncate">{b.name}</div>
                    {b.verified && <BadgeCheck className="h-4 w-4 text-green-600 shrink-0" />}
                    {b.featured && <Sparkles className="h-4 w-4 text-amber-500 shrink-0" />}
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-1 mb-2"><MapPin className="h-3 w-3" /> {b.city}, {b.state}</div>
                  <div className="flex items-center gap-2">
                    <StarRating value={b.rating_avg} size={12} />
                    <span className="text-xs font-semibold text-slate-800">{b.rating_avg.toFixed(1)}</span>
                    <span className="text-xs text-slate-400">({b.rating_count})</span>
                  </div>
                </div>
              </Link>
              <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                {b.featured ? (
                  <Button size="sm" variant="outline" onClick={() => openPortal(b.id)} data-testid={`manage-sub-${b.id}`}>
                    Manage subscription
                  </Button>
                ) : (
                  <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => upgradeListing(b.id)} data-testid={`upgrade-${b.id}`}>
                    <Sparkles className="h-4 w-4 mr-1" /> Upgrade to Premium · $29/mo
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent reviews on my listings */}
      <h2 className="text-2xl font-semibold text-slate-900 mb-4">Recent reviews on your listings</h2>
      {reviews.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-500">No reviews yet on your listings.</div>
      ) : (
        <div className="space-y-3" data-testid="dashboard-reviews">
          {reviews.slice(0, 10).map((r) => (
            <div key={r.id} className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-1">
                <div className="font-semibold text-slate-900">{r.title}</div>
                <StarRating value={r.rating} />
              </div>
              <div className="text-xs text-slate-500 mb-2">by {r.user_name} · {new Date(r.created_at).toLocaleDateString()}</div>
              <p className="text-slate-600 text-sm">{r.body}</p>
            </div>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-listing-dialog">
          <DialogHeader><DialogTitle>Create a new listing</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Business or professional name" required>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="new-name" />
            </Field>
            <Field label="Category" required>
              <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                <SelectTrigger data-testid="new-category-trigger"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="City" required><Input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} data-testid="new-city" /></Field>
            <Field label="State" required><Input value={form.state} onChange={(e) => setForm({...form, state: e.target.value})} data-testid="new-state" /></Field>
            <Field label="Address" className="md:col-span-2"><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} data-testid="new-address" /></Field>
            <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="new-phone" /></Field>
            <Field label="Website"><Input value={form.website} onChange={(e) => setForm({...form, website: e.target.value})} data-testid="new-website" /></Field>
            <Field label="Image URL" className="md:col-span-2"><Input value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} data-testid="new-image" /></Field>
            <Field label="Description" required className="md:col-span-2">
              <Textarea rows={4} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} data-testid="new-description" />
            </Field>
            <label className="md:col-span-2 flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox checked={form.is_professional} onCheckedChange={(v) => setForm({...form, is_professional: !!v})} data-testid="new-is-professional" />
              This is an individual professional (agent, advisor, doctor, etc.)
            </label>
            {form.is_professional && (
              <Field label="Professional title" className="md:col-span-2">
                <Input value={form.professional_title} onChange={(e) => setForm({...form, professional_title: e.target.value})} placeholder="e.g. Realtor, CPA, MD" data-testid="new-title" />
              </Field>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={submitting || !form.name || !form.category || !form.city || !form.state || !form.description}
              className="bg-blue-700 hover:bg-blue-800"
              data-testid="new-listing-submit"
            >
              Create listing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={testid}>
      <div className="text-3xl font-bold text-slate-900">{value}</div>
      <div className="text-sm text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-sm font-medium text-slate-700 block mb-2">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      {children}
    </div>
  );
}
